import { inject, Injectable } from '@angular/core';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly logoutFlagKey = 'ciamcom.admin.logout';
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly eventService = inject(PublicEventsService);

  constructor() {
    this.eventService.registerForEvents().subscribe((event) => {
      if (event.type === EventTypes.SilentRenewFailed) {
        console.error('Silent renew failed, trying to re-authenticate...');
        this.startLoginRedirect(true);
      }

      if (event.type === EventTypes.ConfigLoadingFailed || event.type === EventTypes.CheckingAuthFinishedWithError) {
        console.error('Auth error detected, cleaning URL...');
        this.cleanupUrlParameters();
      }
    });

    this.checkAuthentication(true);
  }

  login(): void {
    sessionStorage.removeItem(AuthService.logoutFlagKey);
    this.startLoginRedirect(true);
  }

  startLoginRedirect(ignoreLogoutFlag = false): void {
    if (this.isAuthenticated()) {
      return;
    }

    if (!ignoreLogoutFlag && this.hasLogoutFlag()) {
      return;
    }

    this.oidcSecurityService.authorize();
  }

  isAuthenticated(): boolean {
    return this.oidcSecurityService.authenticated().isAuthenticated;
  }

  isAuthenticated$(): Observable<boolean> {
    return this.oidcSecurityService.isAuthenticated$.pipe(map(({ isAuthenticated }) => isAuthenticated));
  }

  getUserData(): unknown {
    return this.oidcSecurityService.userData().userData;
  }

  logout(): void {
    sessionStorage.setItem(AuthService.logoutFlagKey, 'true');
    this.oidcSecurityService.logoff().subscribe();
  }

  checkAuthentication(authorizeWhenUnauthenticated = false): void {
    this.oidcSecurityService.checkAuth().subscribe({
      next: ({ isAuthenticated, errorMessage }) => {
        if (errorMessage) {
          console.error('Authentication error:', errorMessage);

          if (errorMessage.toLowerCase().includes('state') || errorMessage.toLowerCase().includes('config')) {
            this.cleanupUrlParameters();
            return;
          }
        }

        if (authorizeWhenUnauthenticated && !isAuthenticated && !errorMessage) {
          this.startLoginRedirect();
          return;
        }

        if (isAuthenticated) {
          sessionStorage.removeItem(AuthService.logoutFlagKey);
        }
      },
      error: (error) => {
        console.error('Error during authentication check:', error);
        this.cleanupUrlParameters();
      },
    });
  }

  private cleanupUrlParameters(): void {
    const urlWithoutParams = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, urlWithoutParams);
  }

  private hasLogoutFlag(): boolean {
    return sessionStorage.getItem(AuthService.logoutFlagKey) === 'true';
  }
}
